import { injectHudThemeStyles } from './hudThemeStyles.js';

const HINT_ID = 'orientation-hint';
const STYLE_ID = 'orientation-hint-styles';
const DISMISS_KEY = 'signalclash:orientationHintDismissed';

// V3 mobile: one-time toast suggesting landscape orientation on portrait
// touch devices. An 8-minute RTS with a wide world map is brutal in portrait
// no matter how much we shrink the HUD — even with the responsive CSS the
// visible battlefield ends up cramped. The hint is dismissible and the
// dismissal is persisted in localStorage so a returning player doesn't see
// the toast at the main menu twice.
//
// Crucially, only the *menu-screen* dismissal is persisted. If the player
// rotates to portrait mid-match the hint re-appears for that session so the
// player gets a reminder even if they dismissed it at the menu. We track this
// via the `sessionShownInMatch` flag: once the hint fires during gameplay it
// is not gated by the stored dismissal.
export class OrientationHint {
  private container: HTMLDivElement | null = null;
  private mediaQuery: MediaQueryList | null = null;
  private resizeHandler: (() => void) | null = null;
  // True after the first call to `showInMatch()`, which bypasses the
  // persisted dismissal so the player still gets a portrait warning even if
  // they dismissed the hint at the main-menu.
  private sessionShownInMatch = false;

  show(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (this.isDismissed()) return;
    if (!this.isCoarse()) return; // Desktop — never show.
    injectStyles();
    this.ensureContainer();
    this.updateVisibility();
    if (this.resizeHandler === null) {
      this.resizeHandler = (): void => this.updateVisibility();
      window.addEventListener('resize', this.resizeHandler);
      window.addEventListener('orientationchange', this.resizeHandler);
    }
  }

  // Call this from GameScene to enable mid-match portrait re-prompting.
  // Unlike `show()`, this bypasses the stored dismissal — once per session
  // (tracked by `sessionShownInMatch`) the hint can re-appear when the player
  // rotates back to portrait during a match, giving them a useful reminder
  // even if they previously dismissed it at the main menu.
  showInMatch(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!this.isCoarse()) return;
    injectStyles();
    this.ensureContainer();
    // Register resize listener the first time we enter match mode.
    if (this.resizeHandler === null) {
      this.resizeHandler = (): void => this.updateVisibilityInMatch();
      window.addEventListener('resize', this.resizeHandler);
      window.addEventListener('orientationchange', this.resizeHandler);
    }
    this.sessionShownInMatch = true;
    this.updateVisibilityInMatch();
  }

  hide(): void {
    if (this.container !== null) this.container.style.display = 'none';
  }

  dispose(): void {
    if (this.resizeHandler !== null) {
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('orientationchange', this.resizeHandler);
      this.resizeHandler = null;
    }
    this.container?.remove();
    this.container = null;
  }

  private isCoarse(): boolean {
    if (typeof window.matchMedia !== 'function') return false;
    if (this.mediaQuery === null) this.mediaQuery = window.matchMedia('(pointer: coarse)');
    return this.mediaQuery.matches;
  }

  private isDismissed(): boolean {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  }

  private persistDismiss(): void {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Private mode / storage disabled — fine, the dismiss is per-session
      // in that case which is still an OK outcome.
    }
  }

  private ensureContainer(): void {
    if (this.container !== null) return;
    const div = document.createElement('div');
    div.id = HINT_ID;
    div.className = 'orient-hint';
    const text = document.createElement('div');
    text.className = 'orient-hint__text';
    text.textContent = 'Rotate your phone to landscape for the best experience.';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'orient-hint__btn';
    btn.textContent = 'Got it';
    btn.addEventListener('click', () => {
      this.persistDismiss();
      this.dispose();
    });
    div.appendChild(text);
    div.appendChild(btn);
    document.body.appendChild(div);
    this.container = div;
  }

  private updateVisibility(): void {
    if (this.container === null) return;
    const portrait = window.innerHeight > window.innerWidth;
    this.container.style.display = portrait && !this.isDismissed() ? 'flex' : 'none';
  }

  // In-match variant: ignores the persisted dismissal so a player who
  // dismissed the hint at the menu still sees a reminder if they rotate
  // to portrait during a match.  The session flag (`sessionShownInMatch`)
  // prevents re-registration of the resize listener.
  private updateVisibilityInMatch(): void {
    if (this.container === null) return;
    const portrait = window.innerHeight > window.innerWidth;
    this.container.style.display = portrait ? 'flex' : 'none';
  }
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${HINT_ID} {
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  display: none;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(8,12,18,0.96);
  border: 1px solid #f5e76e;
  border-radius: 6px;
  z-index: 60;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfd6dd;
  font-size: 12px;
  letter-spacing: 0.04em;
  box-shadow: 0 6px 22px rgba(0,0,0,0.55);
  max-width: calc(100vw - 24px);
  margin-bottom: env(safe-area-inset-bottom, 0);
}
.orient-hint__text { flex: 1; color: #f5e76e; }
.orient-hint__btn {
  font-family: inherit;
  font-size: 11px;
  padding: 6px 12px;
  background: #1f262e;
  color: #eef3f8;
  border: 1px solid #3a444f;
  border-radius: 4px;
  cursor: pointer;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  min-height: 32px;
  touch-action: manipulation;
}
.orient-hint__btn:active { background: #2c3a4a; transform: scale(0.96); }

/* In-match the bottom of the viewport is dominated by the MobileActionBar
 * (bottom:8px, ~60px tall) plus the build panel / armed banner stack. A
 * bottom-anchored hint slides directly behind the action bar in portrait,
 * giving the impression that the HUD elements are stacked on top of each
 * other. Anchor the hint to the TOP on touch viewports, just below the
 * timer/dominance banner band (top:38–60px) so it never collides with the
 * bottom HUD chrome. */
@media (max-width: 820px), (pointer: coarse) {
  #${HINT_ID} {
    bottom: auto;
    top: calc(72px + env(safe-area-inset-top, 0px));
    margin-bottom: 0;
    padding: 8px 12px;
    font-size: 11px;
    max-width: calc(100vw - 16px);
  }
}
  `;
  document.head.appendChild(style);
  injectHudThemeStyles();
}
